import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, ChoicesInterface, WorkflowStepInterface } from '@vendasta/automata';
import { PackageService } from '@vendasta/marketplace-packages';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { ANY_PACKAGE_REGEX } from './constants';
import { InterestOrigin } from './enum';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({
  providedIn: 'root',
})
export class PackageInterestService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly packageService: PackageService,
    private readonly translate: TranslateService,
    private errorHandler: AutomationErrorHandlerService,
  ) {}

  static getPackageIdFromChoices(choices: ChoicesInterface): string {
    const p = JSON.parse(choices?.rules?.[0]?.value || '{}');

    return p?.[1]?.value;
  }

  static getOriginSelectionFromChoices(choices: ChoicesInterface): InterestOrigin {
    const p = JSON.parse(choices?.rules?.[0]?.value || '{}');

    return p?.[0]?.value || InterestOrigin.INTEREST_ORIGIN_VBC_USER;
  }

  getTemplateActionSubtitle(
    _namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    return this.getActionSubtitleEmptyState(step, isWaitUntil);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const c = step.choices;
    const packageId = PackageInterestService.getPackageIdFromChoices(c);

    if (!packageId || packageId === ANY_PACKAGE_REGEX) {
      if (!packageId) {
        this.errorHandler.setValidationError({
          stepId: step?.id,
          taskDefinitionId: step?.taskDefinitionId,
          errorMessage: this.translate.instant(
            'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ACTION.CONFIG_ERROR',
          ),
          isNonDismissable: true,
        });
      }
      return this.getActionSubtitleEmptyState(step, isWaitUntil);
    }

    return this.packageService.get(packageId).pipe(
      switchMap((p) => {
        let textKey =
          'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ACTION.WHEN_CUSTOMER_INTERESTED_IN_SPECIFIC_PACKAGE';
        if (isWaitUntil) {
          textKey =
            'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ACTION.WAIT_UNTIL_CUSTOMER_INTERESTED_IN_SPECIFIC_PACKAGE';
        }
        return this.translate.stream(textKey, { packageName: p.name });
      }),
    );
  }

  getActionSubtitleEmptyState(params: any, isWaitUntil?: boolean): Observable<string> {
    let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ACTION.WHEN_CUSTOMER_INTERESTED_IN_A_PACKAGE';
    if (isWaitUntil) {
      textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ACTION.WAIT_UNTIL_CUSTOMER_INTERESTED_IN_A_PACKAGE';
    }
    return this.translate.stream(textKey);
  }

  getActionSubtitleErrorState(_params: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleSuccessState(_params: any): Observable<string> {
    return of('');
  }

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translate.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }
}
